import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { VirtualSwitch, VirtualSwitchesGetResponse, VirtualSwitchGetResponse } from './../../../app/@shared/models/virtual_switch.model';
import { VirtualSwitchPinnedServerListResponse } from './../../../app/@shared/models/virtual_switch_pinned_server.model';
import { VirtualSwitchProjectConnectionsGetResponse } from './../../../app/@shared/models/virtual_switch_project_connection.model';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, concatMap, map, withLatestFrom, filter, switchMap, tap } from 'rxjs/operators';
import { getAuth } from '../auth/auth.selectors';
import { globalFailure, noopAction } from '../common.actions';
import * as VirtualSwitchesActions from './virtual_switches.actions';
import * as VirtualSwitchesSelectors from './virtual_switches.selectors';
import * as BreakoutActions from './../../../app/@store/breakouts/breakouts.actions';
import * as _ from 'lodash';
import { selectRouteParam } from '../router/router.selector';
import { SDNService } from './../../../app/panel/accounts/sdn/sdn.service';
import { IRequestOptions } from '../request-options.interface';
import { PartnerProjectDetailResponse, PartnerProjectsGetResponse } from './../../../app/@shared/models/partner_project.model';

@Injectable()
export class VirtualSwitchesEffects {
  constructor(private readonly actions$: Actions, private readonly store: Store, private readonly api: PublicPartnerApiService, private readonly SDNService: SDNService) { }

  loadVirtualSwitchesForContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadVirtualSwitches),
      withLatestFrom(
        this.store.select(getAuth),
        this.store.select(VirtualSwitchesSelectors.getListConfig),
        this.store.select(selectRouteParam('account_uuid'))
      ),
      filter(([action, auth, config, accountUuid]) => !!accountUuid),
      switchMap(([action, auth, config, accountUuid]) => {
        // we have a long way getting the virtual switches for a contract...
        return this.api.get<PartnerProjectsGetResponse>('/partners/' + auth!.partner_uuid + '/projects', new HttpParams({ fromObject: { filter: 'contract_uuid=' + accountUuid } })).pipe(
          switchMap(projectList => {
            // we need to get each project details to have the virtual switches

            const projectDetailObservables: Record<string, Observable<PartnerProjectDetailResponse>> = {};

            _.forEach(projectList?.projects, projectLite => {
              projectDetailObservables[projectLite.object_uuid] = this.api.get<PartnerProjectDetailResponse>('/partners/' + auth!.partner_uuid + '/projects/' + projectLite.object_uuid);
            });

            return combineLatest(projectDetailObservables).pipe(
              filter(projectDetails => !!_.find(projectDetails, undefined)), // wait until we have all details fetched (no entry with undefined anymore)
              switchMap(projectDetails => {
                // and for each related virtual switch we now get the details

                const switchObservables: Record<string, Observable<VirtualSwitchGetResponse>> = {};
                _.forEach(projectDetails, projectDetail => {
                  _.forEach(projectDetail.project?.relations?.virtual_switches, virtualSwitchRelation => {
                    switchObservables[virtualSwitchRelation.object_uuid] = this.api.get<VirtualSwitchGetResponse>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + virtualSwitchRelation.object_uuid);
                  });
                });

                if (_.size(switchObservables) === 0) {
                  return of(VirtualSwitchesActions.loadVirtualSwitchesForContractSuccess({
                    accountUuid,
                    data: {},
                    meta: {
                      count: 0,
                      limit: 0,
                      offset: 0,
                      page: 0,
                      total: 0
                    },
                    links: {
                      next: undefined
                    }
                  }));
                }

                return combineLatest(switchObservables).pipe(
                  filter(switchDetails => !!_.find(switchDetails, undefined)),
                  map(switchDetails => {

                    const listData: Record<string, VirtualSwitch> = {};
                    let switchCount = 0;
                    _.forEach(switchDetails, switchDetail => {
                      listData[switchDetail.virtual_switch.object_uuid] = switchDetail.virtual_switch;
                      ++switchCount;
                    });

                    return VirtualSwitchesActions.loadVirtualSwitchesForContractSuccess({
                      accountUuid,
                      data: listData,
                      meta: {
                        count: switchCount,
                        limit: switchCount,
                        offset: 0,
                        page: 0,
                        total: switchCount
                      },
                      links: {
                        next: undefined
                      }
                    });
                  }),
                )
              })
            );
          }),
          catchError(error => of(VirtualSwitchesActions.loadVirtualSwitchesForContractFailure({ accountUuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  loadVirtualSwitches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadVirtualSwitches),
      withLatestFrom(
        this.store.select(getAuth),
        this.store.select(VirtualSwitchesSelectors.getListConfig),
        this.store.select(selectRouteParam('account_uuid'))
      ),
      filter(([action, auth, config, accountUuid]) => !accountUuid),
      switchMap(([action, auth, config, accountUuid]) => {
        return this.SDNService.getSdnFilter$().pipe(
          map(sdnFilter => {
            let newFilter = _.get(config, 'filter', '');
            if (!_.isEmpty(sdnFilter)) {
              if (!_.isEmpty(newFilter)) {
                newFilter += ',';
              }
              newFilter += sdnFilter;
            }

            const newConfig: IRequestOptions = {
              ...config,
              filter: newFilter
            };
            if (newConfig.filter?.length === 0) {
              delete newConfig.filter;
            }

            return [action, auth, newConfig] as [Action<string>, typeof auth, IRequestOptions];
          })
        );
      }),
      concatMap(([action, auth, config]) => {
        return this.api.get<VirtualSwitchesGetResponse>('/partners/' + auth!.partner_uuid + '/virtual_switches', new HttpParams({ fromObject: { ...config } })).pipe(
          map((result) => {
            return VirtualSwitchesActions.loadVirtualSwitchesSuccess({
              data: result.virtual_switches,
              meta: result._meta,
              links: result._links
            });
          }),
          catchError(error => of(VirtualSwitchesActions.loadVirtualSwitchesFailure({ error }), globalFailure({ error })))
        );
      })
    )
  );


  loadVirtualSwitchesNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadVirtualSwitchesNext),

      withLatestFrom(this.store.select(VirtualSwitchesSelectors.getVirtualSwitchesLinks)),

      filter(([_, links]) => links !== undefined && links.next !== undefined),
      concatMap(([action, links]) => {
        if (links?.next?.href) {
          const url = new URL(links.next.href);
          return this.api.get<VirtualSwitchesGetResponse>(url.pathname, {
            page: url.searchParams.get('page') || '0',
            limit: url.searchParams.get('limit') || '10',
            sort: url.searchParams.get('sort') || '',
            filter: url.searchParams.get('filter') ? url.searchParams.get('filter')!.split(',') : [],
            fields: url.searchParams.get('fields') ? url.searchParams.get('fields')!.split(',') : [],
          }).pipe(
            map((result) =>
              VirtualSwitchesActions.loadVirtualSwitchesNextSuccess({
                data: result.virtual_switches || {},
                meta: result._meta,
                links: result._links
              }),
            ),
            catchError(error => of(VirtualSwitchesActions.loadVirtualSwitchesNextFailure({ error }), globalFailure({ error })))
          )
        }
        return of(noopAction());
      })
    )
  );

  loadVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadVirtualSwitch),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.get<VirtualSwitchGetResponse>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.uuid).pipe(
          map((result) => {
            return VirtualSwitchesActions.loadVirtualSwitchSuccess({
              uuid: action.uuid,
              data: result.virtual_switch,
            });
          }),
          catchError(error => of(VirtualSwitchesActions.loadVirtualSwitchesFailure({ error }), globalFailure({ error })))
        );
      })
    )
  );

  assignProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.assignProject),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) =>
        this.api.post<void>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/project_connections', {
          name: action.name,
          project_uuid: action.project_uuid
        }).pipe(
          map(result => VirtualSwitchesActions.assignProjectSuccess({
            project_uuid: action.project_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid
          })),
          catchError(error => of(VirtualSwitchesActions.assignProjectFailure({
            project_uuid: action.project_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid,
            error
          }), globalFailure({ error })))
        )
      )
    )
  );

  unrelateProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.unrelateProject),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) =>
        this.api.delete<void>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/project_connections/' + action.project_connection_uuid).pipe(
          map(result => VirtualSwitchesActions.unrelateProjectSuccess({
            project_connection_uuid: action.project_connection_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid
          })),
          catchError(error => of(VirtualSwitchesActions.unrelateProjectFailure({
            project_connection_uuid: action.project_connection_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid,
            error
          }), globalFailure({ error })))
        )
      )
    )
  );

  createVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.createVirtualSwitch),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) =>
        this.api.post<{ virtual_switch_uuid: string }>('/partners/' + auth!.partner_uuid + '/virtual_switches', {
          name: action.name,
          project_uuid: action.project_uuid
        }).pipe(
          map(result => VirtualSwitchesActions.createVirtualSwitchSuccess({
            object_uuid: result?.virtual_switch_uuid
          })),
          catchError(error => of(VirtualSwitchesActions.createVirtualSwitchFailure({
            error
          }), globalFailure({ error })))
        )
      )
    )
  );

  patchVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.patchVirtualSwitch),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) =>
        this.api.patch<void>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid, action.data as HttpParams).pipe(
          map(() => VirtualSwitchesActions.patchVirtualSwitchSuccess({
            virtual_switch_uuid: action.virtual_switch_uuid
          })),
          catchError(error => of(VirtualSwitchesActions.patchVirtualSwitchFailure({
            virtual_switch_uuid: action.virtual_switch_uuid,
            error
          }), globalFailure({ error })))
        )
      )
    )
  );

  loadProjectConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadProjectConnections),
      withLatestFrom(this.store.select(getAuth)),
      switchMap(([action, auth]) =>
        this.store.select(VirtualSwitchesSelectors.getProjectConnectionsListConfig({ virtual_switch_uuid: action.virtual_switch_uuid })).pipe(
          map(listConfig => [action, auth, listConfig] as [typeof action, typeof auth, IRequestOptions]),
        )
      ),
      concatMap(([action, auth, listConfig]) =>
        this.api.get<VirtualSwitchProjectConnectionsGetResponse>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/project_connections', listConfig as HttpParams).pipe(
          map(result => VirtualSwitchesActions.loadProjectConnectionsSuccess({
            virtual_switch_uuid: action.virtual_switch_uuid,
            data: result.project_connections,
            meta: result._meta,
            links: result._links
          })),
          catchError(error => of(VirtualSwitchesActions.loadProjectConnectionsFailure({
            error,
            virtual_switch_uuid: action.virtual_switch_uuid
          }), globalFailure({ error })))
        )
      )
    )
  );

  loadProjectConnectionsForSelectedVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadProjectConnectionsForSelectedVirtualSwitch),
      withLatestFrom(
        this.store.select(selectRouteParam('virtual_switch_uuid'))
      ),
      map(([action, virtual_switch_uuid]) => VirtualSwitchesActions.loadProjectConnections({ virtual_switch_uuid }))
    )
  );

  patchProjectConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.patchProjectConnection),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) =>
        this.api.patch<void>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/project_connections/' + action.connection_uuid, action.data as HttpParams).pipe(
          map(result => VirtualSwitchesActions.patchProjectConnectionSuccess({
            connection_uuid: action.connection_uuid,
            virtual_switch_uuid: action.virtual_switch_uuid
          })),
          catchError(error => of(VirtualSwitchesActions.patchProjectConnectionFailure({
            error,
            virtual_switch_uuid: action.virtual_switch_uuid,
            connection_uuid: action.connection_uuid
          }), globalFailure({ error })))
        )
      )
    )
  );

  reloadProjectConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VirtualSwitchesActions.patchProjectConnectionSuccess,
        VirtualSwitchesActions.assignProjectSuccess,
        VirtualSwitchesActions.unrelateProjectSuccess
      ),
      map(action => VirtualSwitchesActions.loadProjectConnections({ virtual_switch_uuid: action.virtual_switch_uuid }))
    )
  );

  reloadPinnedServerList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VirtualSwitchesActions.patchPinnedServerSuccess,
        VirtualSwitchesActions.deletePinnedServerSuccess,
      ),
      map(action => VirtualSwitchesActions.loadPinnedServers({ virtual_switch_uuid: action.virtual_switch_uuid }))
    )
  );

  reloadVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VirtualSwitchesActions.assignProjectSuccess,
        VirtualSwitchesActions.unrelateProjectSuccess,
        VirtualSwitchesActions.patchVirtualSwitchSuccess,
        VirtualSwitchesActions.patchPinnedServerSuccess,
        VirtualSwitchesActions.deletePinnedServerSuccess,
        BreakoutActions.patchBreakoutVirtualSwitchSuccess,
        BreakoutActions.addBreakoutVirtualSwitchSuccess,
        BreakoutActions.deleteBreakoutVirtualSwitchSuccess
      ),
      map(action => VirtualSwitchesActions.loadVirtualSwitch({ uuid: action.virtual_switch_uuid }))
    )
  );

  reloadVirtualSwitchesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VirtualSwitchesActions.assignProjectSuccess,
        VirtualSwitchesActions.unrelateProjectSuccess,
        VirtualSwitchesActions.createVirtualSwitchSuccess,
        VirtualSwitchesActions.patchVirtualSwitchSuccess
      ),
      map(action => VirtualSwitchesActions.loadVirtualSwitches())
    )
  );

  loadPinnedServers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadPinnedServers),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.get<VirtualSwitchPinnedServerListResponse>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/pinned_servers').pipe(
          map((result) => {
            return VirtualSwitchesActions.loadPinnedServersSuccess({
              virtual_switch_uuid: action.virtual_switch_uuid,
              data: _.keyBy(result.pinned_servers, s => s.server_uuid),
            });
          }),
          catchError(error => of(VirtualSwitchesActions.loadPinnedServersFailure({ virtual_switch_uuid: action.virtual_switch_uuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  loadPinnedServersForSelectedVirtualSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.loadPinnedServersForSelectedVirtualSwitch),
      withLatestFrom(this.store.select(getAuth), this.store.select(selectRouteParam('virtual_switch_uuid'))),
      tap(([action, auth, virtual_switch_uuid]) => {
        this.store.dispatch(VirtualSwitchesActions.loadPinnedServersForSelectedVirtualSwitchStart({ virtual_switch_uuid }));
      }),
      concatMap(([action, auth, virtual_switch_uuid]) => {
        return this.api.get<VirtualSwitchPinnedServerListResponse>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + virtual_switch_uuid + '/pinned_servers').pipe(
          map((result) => {
            return VirtualSwitchesActions.loadPinnedServersSuccess({
              virtual_switch_uuid,
              data: _.keyBy(result.pinned_servers, s => s.server_uuid),
            });
          }),
          catchError(error => of(VirtualSwitchesActions.loadPinnedServersFailure({ virtual_switch_uuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  patchPinnedServers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.patchPinnedServer),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.patch<void>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/pinned_servers/' + action.server_uuid, {
          ip: action.ip
        }).pipe(
          map((result) => {
            return VirtualSwitchesActions.patchPinnedServerSuccess({
              virtual_switch_uuid: action.virtual_switch_uuid,
              server_uuid: action.server_uuid
            });
          }),
          catchError(error => of(VirtualSwitchesActions.patchPinnedServerFailure({ virtual_switch_uuid: action.virtual_switch_uuid, server_uuid: action.server_uuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  deletePinnedServers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.deletePinnedServer),
      withLatestFrom(this.store.select(getAuth)),
      concatMap(([action, auth]) => {
        return this.api.delete<void>('/partners/' + auth!.partner_uuid + '/virtual_switches/' + action.virtual_switch_uuid + '/pinned_servers/' + action.server_uuid).pipe(
          map((result) => {
            return VirtualSwitchesActions.deletePinnedServerSuccess({
              virtual_switch_uuid: action.virtual_switch_uuid,
              server_uuid: action.server_uuid
            });
          }),
          catchError(error => of(VirtualSwitchesActions.deletePinnedServerFailure({ virtual_switch_uuid: action.virtual_switch_uuid, server_uuid: action.server_uuid, error }), globalFailure({ error })))
        );
      })
    )
  );

  setSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.setSearch),
      withLatestFrom(
        this.store.select(selectRouteParam('account_uuid'))
      ),
      map(([action, accountUuid]) => accountUuid
        ? VirtualSwitchesActions.setSearchForAccount({ search: action.search, account_uuid: accountUuid })
        : VirtualSwitchesActions.setSearchGlobal({ search: action.search })
      )
    )
  );

  setListConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VirtualSwitchesActions.setListConfig),
      withLatestFrom(
        this.store.select(selectRouteParam('account_uuid'))
      ),
      map(([action, accountUuid]) => accountUuid
        ? VirtualSwitchesActions.setListConfigForAccount({ config: action.config, account_uuid: accountUuid })
        : VirtualSwitchesActions.setListConfigGlobal({ config: action.config })
      )
    )
  );

}
